// src/components/dashboard/DashboardOutcomeDetail.tsx
// CORRECTED against real backend.
//
//   - POST /dashboard/outcomes/save handles both create and update
//     (no id → create, id → update) — no PATCH, no GET /:id.
//     Since there's no single-outcome GET, edit mode loads via the
//     list endpoint (POST /dashboard/outcomes) and finds the matching
//     id client-side.
//   - POST /dashboard/outcomes/status with { id, status } in body
//   - POST /dashboard/outcomes/rules with { outcomeId } — per-outcome
//     rules, not a flat global list.
//   - POST /dashboard/outcomes/rules/save creates/updates ONE rule at a
//     time (id optional → create).
//   - Performance tab REMOVED entirely — no execution engine (Phase 2,
//     deliberately deferred).
//   - completionCriteria and linkedCapabilityId are real fields, stored-
//     but-inert until Phase 2.
//
//   - Outcome delete wired to POST /dashboard/outcomes/delete
//     (tools/delete-outcome.ts) via the real AlertDialog primitive.
//   - NEW — per-rule delete wired to POST /dashboard/outcomes/rules/delete
//     (tools/delete-outcome-rule.ts). Previously the only way to remove
//     one unwanted rule was deleting the entire outcome and losing every
//     other rule with it. Each rule row gets an inline delete affordance;
//     ONE shared AlertDialog handles confirmation for whichever rule was
//     selected (ruleToDelete), rather than mounting a separate dialog per
//     rule in the list.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
} from '../ui/card';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { ChevronLeft, Trash2, X } from 'lucide-react';
import { authedFetch } from '../../services/dashboardApi';

type OutcomeStatus = 'draft' | 'live' | 'paused';
type WorkMode = 'observe' | 'ask_first' | 'complete_within_limits';
type RuleCategory = 'qualification' | 'scope_limit' | 'escalation' | 'data_access' | 'other';

interface PriceModel {
  kind: 'fixed' | 'hourly' | 'range';
  amountCents: number | null;
  minCents: number | null;
  maxCents: number | null;
}

interface Outcome {
  id: string;
  name: string;
  description: string;
  completionCriteria: string | null;
  priceModel: PriceModel;
  priceUnit: string | null;
  defaultWorkMode: WorkMode;
  linkedCapabilityId: string | null;
  status: OutcomeStatus;
}

interface OutcomeRule {
  id: string;
  label: string;
  category: RuleCategory;
  statement: string;
}

interface FormState {
  name: string;
  description: string;
  completionCriteria: string;
  priceModelKind: PriceModel['kind'];
  amountCents: number | null;
  minCents: number | null;
  maxCents: number | null;
  priceUnit: string;
  defaultWorkMode: WorkMode;
  status: OutcomeStatus;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  completionCriteria: '',
  priceModelKind: 'fixed',
  amountCents: null,
  minCents: null,
  maxCents: null,
  priceUnit: 'per package',
  defaultWorkMode: 'ask_first',
  status: 'draft',
};

const WORK_MODE_LABELS: Record<WorkMode, string> = {
  observe: 'Observe',
  ask_first: 'Ask First',
  complete_within_limits: 'Complete Within Limits',
};

const RULE_CATEGORY_LABELS: Record<RuleCategory, string> = {
  qualification: 'Qualification',
  scope_limit: 'Scope limit',
  escalation: 'Escalation trigger',
  data_access: 'Data access',
  other: 'Other',
};

const STATUS_STYLES: Record<OutcomeStatus, string> = {
  draft: 'bg-brand-surface text-brand-gray',
  live: 'bg-green-50 text-green-700',
  paused: 'bg-amber-50 text-amber-700',
};

const STATUS_LABELS: Record<OutcomeStatus, string> = {
  draft: 'Draft',
  live: 'Live',
  paused: 'Paused',
};

export const DashboardOutcomeDetail: React.FC = () => {
  const { outcomeId } = useParams();
  const navigate = useNavigate();
  const isCreate = !outcomeId || outcomeId === 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [rules, setRules] = useState<OutcomeRule[]>([]);
  const [newRule, setNewRule] = useState({ label: '', category: 'qualification' as RuleCategory, statement: '' });
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<OutcomeRule | null>(null);
  const [isDeletingRule, setIsDeletingRule] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isCreate) return;

      try {
        const res = await authedFetch('/outcomes', { method: 'POST' });
        if (!res.ok) throw new Error('Failed to load packages.');
        const data = await res.json();
        const found: Outcome | undefined = (data.outcomes ?? []).find((o: Outcome) => o.id === outcomeId);
        if (!found) throw new Error('This outcome could not be found.');

        if (!cancelled) {
          setForm({
            name: found.name,
            description: found.description,
            completionCriteria: found.completionCriteria ?? '',
            priceModelKind: found.priceModel.kind,
            amountCents: found.priceModel.amountCents,
            minCents: found.priceModel.minCents,
            maxCents: found.priceModel.maxCents,
            priceUnit: found.priceUnit ?? '',
            defaultWorkMode: found.defaultWorkMode,
            status: found.status,
          });
        }

        const rulesRes = await authedFetch('/outcomes/rules', {
          method: 'POST',
          body: JSON.stringify({ outcomeId }),
        });
        if (rulesRes.ok) {
          const rulesData = await rulesRes.json();
          if (!cancelled) setRules(rulesData.rules ?? []);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load this outcome.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [outcomeId, isCreate]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const res = await authedFetch('/outcomes/save', {
        method: 'POST',
        body: JSON.stringify({
          ...(isCreate ? {} : { id: outcomeId }),
          name: form.name,
          description: form.description,
          completionCriteria: form.completionCriteria || undefined,
          priceModel: {
            kind: form.priceModelKind,
            amountCents: form.amountCents,
            minCents: form.minCents,
            maxCents: form.maxCents,
          },
          priceUnit: form.priceUnit || undefined,
          defaultWorkMode: form.defaultWorkMode,
          status: form.status,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save this outcome.');
      }
      const data = await res.json();
      navigate(`/dashboard/outcomes/${data.outcome.id}`);
    } catch (err: any) {
      setError(err.message || 'Something went wrong saving this outcome.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (isCreate) return;
    const next: OutcomeStatus = form.status === 'live' ? 'paused' : 'live';
    const prevStatus = form.status;
    setForm((f) => ({ ...f, status: next }));
    try {
      const res = await authedFetch('/outcomes/status', {
        method: 'POST',
        body: JSON.stringify({ id: outcomeId, status: next }),
      });
      if (!res.ok) throw new Error('Failed to update status.');
    } catch (err: any) {
      setForm((f) => ({ ...f, status: prevStatus }));
      setError(err.message || 'Could not update status.');
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const res = await authedFetch('/outcomes/delete', {
        method: 'POST',
        body: JSON.stringify({ id: outcomeId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete this package.');
      }
      navigate('/dashboard/outcomes');
    } catch (err: any) {
      setError(err.message || 'Could not delete this package.');
      setIsDeleting(false);
    }
  };

  const handleAddRule = async () => {
    if (isCreate || !newRule.label.trim() || !newRule.statement.trim()) return;
    setIsSavingRule(true);
    try {
      const res = await authedFetch('/outcomes/rules/save', {
        method: 'POST',
        body: JSON.stringify({ outcomeId, ...newRule }),
      });
      if (!res.ok) throw new Error('Failed to save rule.');
      const data = await res.json();
      setRules((prev) => [...prev, { id: data.rule.id, ...newRule }]);
      setNewRule({ label: '', category: 'qualification', statement: '' });
    } catch (err: any) {
      setError(err.message || 'Could not save this rule.');
    } finally {
      setIsSavingRule(false);
    }
  };

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return;
    setIsDeletingRule(true);
    setError(null);
    try {
      const res = await authedFetch('/outcomes/rules/delete', {
        method: 'POST',
        body: JSON.stringify({ outcomeId, id: ruleToDelete.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete rule.');
      }
      setRules((prev) => prev.filter((r) => r.id !== ruleToDelete.id));
      setRuleToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Could not delete this rule.');
    } finally {
      setIsDeletingRule(false);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-brand-gray font-mono">Loading outcome…</p>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/outcomes')}
          className="flex items-center gap-1.5 -ml-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Packages
        </Button>

        {!isCreate && (
          <Badge className={STATUS_STYLES[form.status]}>{STATUS_LABELS[form.status]}</Badge>
        )}
      </div>

      <div>
        <h1 className="text-lg font-semibold text-brand-black">
          {isCreate ? 'Define a new work package' : form.name || 'Edit package'}
        </h1>
        <p className="text-sm text-brand-charcoal font-light mt-1">
          {isCreate
            ? 'Package a scoped piece of work agents can discover and request under your rules.'
            : 'Edit scope, pricing, and rules for this outcome.'}
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Tabs defaultValue="scope" orientation="horizontal" className="flex-col!">
         <TabsList className="inline-flex! w-fit! flex-row!">
           <TabsTrigger value="scope">Scope</TabsTrigger>
           <TabsTrigger value="rules">Rules</TabsTrigger>
         </TabsList>

        {/* ── SCOPE ─────────────────────────────────────────────── */}
        <TabsContent value="scope" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Basics</CardTitle>
              <CardDescription>What this outcome is and what it delivers.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="outcome-name">Name</Label>
                <Input
                  id="outcome-name"
                  placeholder="e.g., Standard Mutual NDA Review"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outcome-description">Description</Label>
                <Textarea
                  id="outcome-description"
                  placeholder="What an agent is requesting when it triggers this outcome."
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outcome-criteria">Completion criteria</Label>
                <Textarea
                  id="outcome-criteria"
                  placeholder="What has to be true for this outcome to count as done — e.g., redline delivered, summary attached, no unresolved exceptions."
                  value={form.completionCriteria}
                  onChange={(e) => setForm((f) => ({ ...f, completionCriteria: e.target.value }))}
                  rows={3}
                />
                <p className="text-xs text-brand-gray font-light">
                  Stored for reference only right now — there's no automated execution yet to check work against this.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
              <CardDescription>What agents see when they check whether this outcome fits their budget.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="outcome-price-kind">Pricing type</Label>
                <Select
                  value={form.priceModelKind}
                  onValueChange={(v: PriceModel['kind']) => setForm((f) => ({ ...f, priceModelKind: v }))}
                >
                  <SelectTrigger className="w-full" id="outcome-price-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="range">Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.priceModelKind === 'range' ? (
                <div className="flex gap-4">
                  <div className="space-y-1.5 flex-1">
                    <Label htmlFor="outcome-price-min">Minimum</Label>
                    <Input
                      id="outcome-price-min"
                      type="number"
                      value={form.minCents != null ? form.minCents / 100 : ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          minCents: e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5 flex-1">
                    <Label htmlFor="outcome-price-max">Maximum</Label>
                    <Input
                      id="outcome-price-max"
                      type="number"
                      value={form.maxCents != null ? form.maxCents / 100 : ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          maxCents: e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                        }))
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="outcome-price-amount">
                    {form.priceModelKind === 'hourly' ? 'Rate' : 'Price'}
                  </Label>
                  <Input
                    id="outcome-price-amount"
                    type="number"
                    placeholder="500"
                    value={form.amountCents != null ? form.amountCents / 100 : ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        amountCents: e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                      }))
                    }
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="outcome-price-unit">Unit</Label>
                <Input
                  id="outcome-price-unit"
                  placeholder="per package"
                  value={form.priceUnit}
                  onChange={(e) => setForm((f) => ({ ...f, priceUnit: e.target.value }))}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Default work mode</CardTitle>
              <CardDescription>
                How much an agent can do on this outcome without stopping for your approval. Stored for future use — enforcement isn't live yet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={form.defaultWorkMode}
                onValueChange={(v: WorkMode) => setForm((f) => ({ ...f, defaultWorkMode: v }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(WORK_MODE_LABELS) as WorkMode[]).map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {WORK_MODE_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── RULES (per-outcome) ──────────────────────────────────── */}
        <TabsContent value="rules" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Rules for this outcome</CardTitle>
              <CardDescription>
                Boundaries scoped to this outcome specifically — who can trigger it, what's out of scope, when it escalates to you.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isCreate ? (
                <p className="text-sm text-brand-gray font-light">
                  Save this outcome first, then come back to add rules.
                </p>
              ) : (
                <>
                  {rules.length === 0 ? (
                    <p className="text-sm text-brand-gray font-light">No rules added yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {rules.map((rule) => (
                        <li key={rule.id} className="rounded-lg border border-brand-border p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-brand-black">{rule.label}</span>
                                <span className="text-xs font-mono text-brand-gray bg-brand-surface rounded-full px-2 py-0.5">
                                  {RULE_CATEGORY_LABELS[rule.category]}
                                </span>
                              </div>
                              <p className="text-sm text-brand-charcoal font-light">{rule.statement}</p>
                            </div>
                            <button
                              onClick={() => setRuleToDelete(rule)}
                              className="shrink-0 text-brand-gray hover:text-red-600 transition-colors p-1"
                              aria-label={`Delete rule: ${rule.label}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="pt-4 border-t border-brand-border space-y-3">
                    <p className="text-xs font-mono uppercase tracking-widest text-brand-gray">Add a rule</p>
                    <div className="space-y-1.5">
                      <Label htmlFor="rule-label">Label</Label>
                      <Input
                        id="rule-label"
                        placeholder="e.g., Escalate unusual clauses"
                        value={newRule.label}
                        onChange={(e) => setNewRule((r) => ({ ...r, label: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rule-category">Category</Label>
                      <Select
                        value={newRule.category}
                        onValueChange={(v: RuleCategory) => setNewRule((r) => ({ ...r, category: v }))}
                      >
                        <SelectTrigger className="w-full" id="rule-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(RULE_CATEGORY_LABELS) as RuleCategory[]).map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {RULE_CATEGORY_LABELS[cat]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="rule-statement">Statement</Label>
                      <Textarea
                        id="rule-statement"
                        placeholder="Describe the boundary in plain language."
                        value={newRule.statement}
                        onChange={(e) => setNewRule((r) => ({ ...r, statement: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddRule}
                      disabled={isSavingRule || !newRule.label.trim() || !newRule.statement.trim()}
                    >
                      {isSavingRule ? 'Adding…' : 'Add rule'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── ACTIONS ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-4 border-t border-brand-border">
        <div>
          {!isCreate && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isDeleting}
                className="text-red-600 hover:text-red-700 flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? 'Deleting…' : 'Delete outcome'}
              </Button>
              <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this package?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes "{form.name}" and all {rules.length} of its rules. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={confirmDelete}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isCreate && form.status !== 'draft' && (
            <Button variant="outline" onClick={toggleStatus}>
              {form.status === 'live' ? 'Pause' : 'Publish'}
            </Button>
          )}
          <Button variant="primary" onClick={handleSave} disabled={isSaving || !form.name.trim()}>
            {isSaving ? 'Saving…' : isCreate ? 'Create package' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Shared dialog for per-rule delete confirmation — one instance,
          not one per rule, driven by ruleToDelete state. */}
      <AlertDialog open={!!ruleToDelete} onOpenChange={(open) => !open && setRuleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes "{ruleToDelete?.label}" from this package. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDeleteRule} disabled={isDeletingRule}>
              {isDeletingRule ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};